import { showErrorToast } from "@src/appV2/lib/Notifications";
// eslint-disable-next-line no-restricted-imports -- Need to access Redux session state
import type { SessionType } from "@src/modules/interface";
import store from "@src/store";

/**
 * Waits for Redux session to be initialized after Firebase authentication.
 *
 * This function subscribes to the Redux store and resolves when `session.userId`
 * is populated. Includes a 5-second timeout to prevent indefinite waiting.
 *
 * @returns Promise that resolves when session is ready, or rejects on timeout
 */
export async function waitForSession(): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    // Check if session is already initialized
    const currentState = store.getState() as SessionType;

    if (currentState.session?.userId) {
      resolve();
      return;
    }

    // eslint-disable-next-line prefer-const -- timeoutId is assigned after subscribe to avoid circular reference
    let timeoutId: NodeJS.Timeout;

    // Subscribe to Redux store
    const unsubscribe = store.subscribe(() => {
      const state = store.getState() as SessionType;
      const userId = state.session?.userId;

      if (userId) {
        unsubscribe();
        clearTimeout(timeoutId);
        resolve();
      }
    });

    // Timeout fallback - if Redux doesn't update in 10 seconds, reject
    // Increased from 5 to 10 seconds to account for slow API calls
    timeoutId = setTimeout(() => {
      unsubscribe();
      const error = "Login successful but session failed to initialize. Please try again.";
      showErrorToast(error);
      reject(new Error(error));
    }, 10_000);
  });
}
